/* eslint-disable @typescript-eslint/no-explicit-any */
import SVGEmail from "@/assets/icons/SVGEmail.tsx";
import { Alert, Button } from "@mui/material";
import { sendPerformanceIncentiveEmailReq } from "@/api/projectsApi";
import React, { FC, useMemo, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import { useProjectsStore } from "@/store/projectsStore";
import { ProjectRecord } from "@/types";

type NotificationMessage = {
  message: string;
  severity: "error" | "success";
};

type Props = {
  projectIds: number[] | undefined;
};

const SendPerformanceIncentiveEmailButton: FC<Props> = ({ projectIds }) => {
  const [notificationMessage, setNotificationMessage] =
    useState<NotificationMessage | null>(null);
  const { allProjects } = useProjectsStore((state) => state.allProjects);

  const disabled = useMemo(() => {
    if (!projectIds || projectIds.length === 0) {
      return true;
    }

    if (!allProjects) {
      return true;
    }

    const projectsByIds = allProjects.filter((project: ProjectRecord) =>
      projectIds.includes(project.id)
    );

    if (projectsByIds.length === 0) {
      return true;
    }

    const hasAtLeastOneProjectWithPerformanceYear = projectsByIds.some(
      (project: ProjectRecord) => {
        const projectDocumentations = project?.ProjectDocumentations ?? [];

        return projectDocumentations.some(
          (doc) =>
            doc.performanceYear !== null && doc.performanceYear !== undefined
        );
      }
    );

    return !hasAtLeastOneProjectWithPerformanceYear;
  }, [allProjects, projectIds]);

  const sendPerformanceIncentiveEmailForProject =
    (projectIds: number[]) => async () => {
      try {
        const response = await sendPerformanceIncentiveEmailReq(projectIds);
        setNotificationMessage({
          message: response?.data?.message,
          severity: "success",
        });
      } catch (e: any) {
        setNotificationMessage({
          message: e?.response?.data?.error || "An unexpected error occurred.",
          severity: "error",
        });
      }
    };

  const handleClose = () => {
    setNotificationMessage(null);
  };

  if (!projectIds) return null;

  return (
    <>
      <Button
        variant="outlined"
        style={{ flexShrink: 0 }}
        onClick={sendPerformanceIncentiveEmailForProject(projectIds)}
        startIcon={<SVGEmail color={disabled ? "gray" : "#1976d2"} />}
        disabled={disabled}
      >
        Send Performance Incentive Email
      </Button>
      {notificationMessage && (
        <Snackbar
          open={Boolean(notificationMessage)}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert onClose={handleClose} severity={notificationMessage?.severity}>
            {notificationMessage?.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default SendPerformanceIncentiveEmailButton;
